import React, { FormEvent, useRef, useState } from "react";
import { ArrowRight, Link2 } from "lucide-react";
import { validateLandingYoutubeUrl } from "../../lib/landingSourceIntent";

export type StartClipping = (sourceUrl?: string) => void | Promise<void>;

interface HeroSectionProps {
  onStartClipping: StartClipping;
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  onStartClipping,
}) => {
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isRouting, setIsRouting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const actionInFlightRef = useRef(false);

  const runOnce = async (action: () => void | Promise<void>) => {
    if (actionInFlightRef.current) return;
    actionInFlightRef.current = true;
    setIsRouting(true);
    try {
      await action();
    } finally {
      actionInFlightRef.current = false;
      setIsRouting(false);
    }
  };

  const submitSource = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const validation = validateLandingYoutubeUrl(youtubeUrl);
    if (!validation.ok) {
      setError(validation.message);
      inputRef.current?.focus();
      return;
    }
    setError(null);
    await runOnce(() => onStartClipping(validation.url));
  };

  const startWithoutSource = async () => {
    setError(null);
    await runOnce(() => onStartClipping());
  };

  return (
    <section
      className="relative isolate overflow-hidden px-5 pb-[4.5rem] pt-16 sm:px-6 sm:pb-[6.5rem] sm:pt-20 lg:px-8 lg:pb-[7.5rem] lg:pt-24"
      aria-labelledby="landing-hero-title"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-[-31rem] -z-20 h-[72rem] w-[72rem] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(90,214,255,0.55)_0%,rgba(61,123,255,0.25)_38%,rgba(255,255,255,0)_70%)] blur-2xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-[-23rem] -z-10 h-[58rem] w-[58rem] -translate-x-1/2 rounded-full border border-blue-300/45"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-[-10rem] -z-10 h-[36rem] w-[36rem] -translate-x-1/2 rounded-full border border-cyan-200/70"
      />

      <div className="mx-auto max-w-[76rem] text-center">
        <button
          type="button"
          data-hero-secondary-cta
          disabled={isRouting}
          onClick={() => void startWithoutSource()}
          className="inline-flex min-h-12 items-center gap-2 rounded-2xl border border-blue-200 bg-white/85 px-5 text-lg font-bold text-[var(--tc-blue)] shadow-[0_12px_34px_rgba(22,119,255,0.14)] backdrop-blur-sm transition hover:-translate-y-0.5 hover:border-blue-300 hover:bg-white hover:shadow-[0_16px_38px_rgba(22,119,255,0.20)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-4 disabled:opacity-60"
        >
          Start creating now
          <ArrowRight aria-hidden="true" className="h-5 w-5" />
        </button>

        <h1
          id="landing-hero-title"
          className="tc-display mx-auto mt-9 text-[2.65rem] leading-[0.98] sm:mt-10 sm:text-[3.8rem] lg:text-[5.25rem] xl:text-[5.7rem]"
        >
          <span className="block whitespace-normal text-[var(--tc-ink)] sm:whitespace-nowrap">
            Turn YouTube videos into
          </span>
          <span className="mt-1 block text-[var(--tc-blue)]">
            viral-ready clips
          </span>
        </h1>

        <p className="mx-auto mt-7 max-w-[52rem] text-balance text-lg leading-8 text-[var(--tc-ink-muted)] sm:text-xl">
          Paste a YouTube link. TopClip finds the strongest moments and gives you
          clips ready to post.
        </p>

        <form className="mx-auto mt-12 max-w-3xl sm:mt-14" onSubmit={submitSource} noValidate>
          <div
            className={`grid gap-3 rounded-[var(--tc-radius-control)] border bg-white p-2.5 shadow-[var(--tc-shadow-control)] transition sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center ${
              error
                ? "border-red-300"
                : "border-[var(--tc-border)] focus-within:border-blue-400"
            }`}
          >
            <label className="sr-only" htmlFor="landing-youtube-url">
              Paste a YouTube link
            </label>
            <div className="flex min-w-0 items-center gap-3 px-2 sm:px-3">
              <Link2 aria-hidden="true" className="h-6 w-6 flex-none text-blue-500" />
              <input
                ref={inputRef}
                id="landing-youtube-url"
                type="url"
                inputMode="url"
                autoComplete="url"
                spellCheck={false}
                value={youtubeUrl}
                onChange={(event) => {
                  setYoutubeUrl(event.target.value);
                  if (error) setError(null);
                }}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? "landing-youtube-error" : undefined}
                placeholder="Paste a YouTube link"
                className="tc-hero-input h-12 min-w-0 flex-1 border-0 bg-transparent px-0 text-lg font-medium text-[var(--tc-ink)] placeholder:text-slate-400 focus:translate-y-0 focus:border-transparent focus:bg-transparent focus:ring-0 sm:h-14 sm:text-xl"
              />
            </div>
            <button
              type="submit"
              disabled={isRouting}
              className="tc-primary-action inline-flex min-h-14 items-center justify-center gap-2 rounded-[calc(var(--tc-radius-control)-0.35rem)] bg-[var(--tc-blue)] px-6 text-lg font-bold text-white shadow-[var(--tc-shadow-cta)] transition hover:bg-[var(--tc-blue-hover)] disabled:cursor-wait disabled:opacity-70 sm:min-w-[12rem] sm:text-xl"
            >
              {isRouting ? "Opening TopClip..." : "Make my clips"}
              {!isRouting && <ArrowRight aria-hidden="true" className="h-6 w-6" />}
            </button>
          </div>
          <div className="mt-3 min-h-6">
            {error && (
              <p
                id="landing-youtube-error"
                role="alert"
                className="text-sm font-semibold text-red-700"
              >
                {error}
              </p>
            )}
          </div>
        </form>
      </div>
    </section>
  );
};
